import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Grid,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
} from "@material-ui/core";
import PaginateTable from "components/helpers/PaginateTable";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import { useHistory } from "react-router-dom";
import TextTypo from "components/helpers/TextTypo";
import { loanApplicationAction } from "redux/actions/applicationAction";
import SimpleTabs from "components/helpers/SimpleTabs";
import Table from "components/Table";
import Radio from "components/Radio";
import SearchInput from "components/Input/Search";
import Button from "components/Button";
import { RemoveRedEye } from "@material-ui/icons";
import { Tag } from "antd";
import { getStatusColor, applicantFilters, decodeToken } from "utils";
import moment from "moment";
import Filter from "components/Filter";

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
});

const ViewApplication = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [applicationId, setApplicationId] = useState();
  const [searchOption, setSearchOption] = useState("names");
  const [filterBy, setFilterBy] = useState({});

  const [anchorEl, setAnchorEl] = useState(null);
  const { loanApplications, applicationLoading } = useSelector(
    ({ loanApplications }) => ({
      loanApplications: loanApplications?.data,
      applicationLoading: loanApplications?.loading,
    })
  );

  const level =
    decodeToken()?.userRole?.RoleName === "Bank Officer" ? "bank" : "backend";

  const queryData = useCallback((newPage = page, data) => {
    const payload = {
      page: newPage,
      limit: rowsPerPage,
      level: level === "bank" ? "bank" : undefined,
      ...data,
    };
    dispatch(loanApplicationAction(payload));
  }, []);

  const columns = [
    {
      id: "Names",
      dataIndex: "applicant",
      title: `${t("Names")}`,
      render: ({ firstName, lastName }) => firstName + " " + lastName,
    },
    {
      id: "nationalId",
      dataIndex: "applicant",
      title: `${t("id-number")}`,
      render: ({ nationalId }) => nationalId,
    },
    {
      id: "phone",
      dataIndex: "propertyValueLimit",
      title: `Property Value Limit`,
      render: (propertyValueLimit) =>
        "RWF " + propertyValueLimit?.toLocaleString(),
    },
    {
      id: "referenceId",
      dataIndex: "referenceId",
      title: `${t("reference-id")}`,
    },
    {
      id: "views",
      dataIndex: "views",
      title: `Views`,
      render: (views) => views?.total?.toLocaleString() || 0,
    },
    {
      id: "lastView",
      dataIndex: "lastView",
      title: `Last Viewed`,
      render: (lastView) =>
        lastView?.lastViewDate
          ? moment(lastView?.lastViewDate).from()
          : "Never",
    },
    {
      id: "action",
      dataIndex: "id",
      title: `${t("action")}`,
      render: (id, record) => {
        return (
          <Button
            onClick={() => history.push(`/applications/${id}`)}
            icon={<RemoveRedEye />}
          />
        );
      },
    },
  ];

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const viewApplication = () => {
    history.push(`/applications/${applicationId}`);
  };

  const handleSearch = (value) => {
    let payload = {};

    switch (searchOption) {
      case "referenceId":
        payload.referenceId = value;
        break;

      case "nationalId":
        payload.nationalId = value;
        break;

      case "names":
        payload.names = value;
        break;

      default:
        payload = {};
    }
    dispatch(loanApplicationAction(payload));
  };

  const onSearchChange = (e) => {
    const { value } = e.target;

    if (!value) {
      dispatch(loanApplicationAction());
    }
  };

  useEffect(() => {
    const fetchData = () => {
      queryData(undefined, filterBy);
    };
    fetchData();
  }, [filterBy]);

  const handleChangePage = (page, limit) => {
    dispatch(loanApplicationAction({ page: page - 1, limit }));
  };

  return (
    <div style={{ marginTop: 20 }}>
      <Container>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <TextTypo
              text={`${t("application")} (${
                loanApplications?.loanApplications?.count
              })`}
              gutterBottom={true}
              variant="h4"
            />
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6} md={8}>
                <Radio
                  options={[
                    { label: "Names", value: "names" },
                    { label: "National Id", value: "nationalId" },
                    { label: "Reference ID", value: "referenceId" },
                  ]}
                  value={searchOption}
                  onChange={(e) => setSearchOption(e.target.value)}
                />
                <SearchInput
                  placeholder={
                    searchOption === "nationalId"
                      ? "Ex: 1192770003989043"
                      : searchOption === "referenceId"
                      ? "Ex: W42Q5LV0LY"
                      : "Ex: Nyirambonabushya"
                  }
                  onSearch={handleSearch}
                  onChange={onSearchChange}
                  loading={applicationLoading}
                  className="my-3 w-75"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.root}>
            <Grid container direction="row">
              <Grid xs={3}>
                <Filter
                  filterOptions={applicantFilters}
                  filterBy={filterBy}
                  setFilterBy={setFilterBy}
                />
              </Grid>
              <Grid xs={9}>
                <Table
                  columns={columns}
                  data={loanApplications?.loanApplications?.rows}
                  loading={applicationLoading}
                  total={loanApplications?.totalCount}
                  onPageChange={handleChangePage}
                />

                <Menu
                  id="simple-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleCloseMenu}
                >
                  <MenuItem onClick={viewApplication}>{t("view")}</MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default ViewApplication;
